import React from 'react';
const { useState, useEffect, useMemo } = React;
import { inputSt, cardSt, mutedSt, badgeSt, warnSt, fmtDate } from '../diagnostic/shared.jsx';
import { LIFE_KINDS, LIFE_STATUSES, KNOWLEDGE_LEVELS, CONTENT_STATUSES, LIFE_TEXT_LIMITS, ownerLabel } from '../../ownerSpaceConfig.js';
import { ownerCall, LevelBadge, listBtnSt } from './shared.jsx';
import ItemDetail from './ItemDetail.jsx';

// Biblioteca de vida: what I read, listen to and learn. A saved title or link
// is only a reference: the level of each material says what Oike really knows.

export function QuickAdd({ onAdded }) {
  const [v, setV] = useState({ title: '', kind: 'book', reference: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const add = async (e) => {
    e.preventDefault();
    if (!v.title.trim()) return;
    setBusy(true); setError('');
    try { const d = await ownerCall({ action: 'saveItem', item: { ...v, status: 'pending' } }); setV({ title: '', kind: v.kind, reference: '' }); onAdded(d.item); }
    catch (err) { setError(err.message); }
    setBusy(false);
  };
  return (
    <form onSubmit={add} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
      <input style={{ ...inputSt, flex: '2 1 220px', width: 'auto' }} placeholder="Título del libro, artículo, podcast…" value={v.title} maxLength={LIFE_TEXT_LIMITS.title} onChange={e => setV({ ...v, title: e.target.value })} aria-label="Título" />
      <select style={{ ...inputSt, width: 'auto', flex: '0 0 auto' }} value={v.kind} onChange={e => setV({ ...v, kind: e.target.value })} aria-label="Tipo">
        {LIFE_KINDS.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}
      </select>
      <input style={{ ...inputSt, flex: '1 1 180px', width: 'auto' }} placeholder="Enlace o referencia (opcional)" value={v.reference} maxLength={LIFE_TEXT_LIMITS.reference} onChange={e => setV({ ...v, reference: e.target.value })} aria-label="Enlace o referencia" />
      <button className="action-btn btn-primary" disabled={busy || !v.title.trim()} type="submit">+ Agregar</button>
      {error && <div style={{ ...warnSt, padding: 8, margin: 0, width: '100%' }}>{error}</div>}
    </form>
  );
}

export default function LifeLibrary({ onAskAbout }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState(null);
  const [f, setF] = useState({ q: '', kind: '', status: '', level: '', topic: '' });
  const load = () => ownerCall({ action: 'listLibrary' }).then(d => setItems(d.items)).catch(e => setError(e.message));
  useEffect(() => { load(); }, []);

  const topics = useMemo(() => [...new Set((items || []).flatMap(i => [...i.topics, ...i.tags]))].sort(), [items]);
  const shown = useMemo(() => {
    const q = f.q.trim().toLowerCase();
    return (items || []).filter(i => (!f.kind || i.kind === f.kind) && (!f.status || i.status === f.status) && (!f.level || i.level === f.level)
      && (!f.topic || [...i.topics, ...i.tags].includes(f.topic))
      && (!q || [i.title, i.creator, ...i.topics, ...i.tags].some(x => (x || '').toLowerCase().includes(q))));
  }, [items, f]);
  const sel = (k, list, all) => (
    <select style={{ ...inputSt, width: 'auto' }} value={f[k]} onChange={e => setF({ ...f, [k]: e.target.value })} aria-label={all}>
      <option value="">{all}</option>
      {list.map(x => <option key={x.key} value={x.key}>{x.label}</option>)}
    </select>
  );

  if (openId) return <ItemDetail id={openId} onBack={() => { setOpenId(null); load(); }} onDeleted={() => { setOpenId(null); load(); }} onAskAbout={onAskAbout} />;

  return (
    <div>
      <div style={cardSt}>
        <QuickAdd onAdded={(item) => { setItems(list => [item, ...(list || [])]); }} />
        <div style={{ ...mutedSt, marginTop: 8 }}>Agregar un título o un enlace guarda solo la referencia: no significa que lo hayas leído o escuchado, y la IA no conoce su contenido hasta que aportes notas, fragmentos o el contenido.</div>
      </div>
      {error && <div style={{ ...warnSt, padding: 10 }}>{error}</div>}
      <div style={cardSt}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          <input style={{ ...inputSt, flex: '1 1 200px', width: 'auto' }} placeholder="Buscar por título, autor o tema…" value={f.q} onChange={e => setF({ ...f, q: e.target.value })} aria-label="Buscar" />
          {sel('kind', LIFE_KINDS, 'Todos los tipos')}
          {sel('status', LIFE_STATUSES, 'Todos los estados')}
          {sel('level', KNOWLEDGE_LEVELS, 'Todo lo que conocemos')}
          {topics.length > 0 && sel('topic', topics.map(t => ({ key: t, label: t })), 'Todos los temas')}
        </div>
        {!items ? <div style={mutedSt}>Cargando…</div> : !items.length ? <div style={mutedSt}>Tu biblioteca está vacía. Agrega lo que estás leyendo, escuchando o estudiando.</div>
          : !shown.length ? <div style={mutedSt}>No hay materiales con esos filtros.</div> : shown.map(i => (
            <button key={i.id} style={listBtnSt} onClick={() => setOpenId(i.id)}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                <b style={{ fontSize: 13 }}>{i.title}</b>{i.creator && <span style={mutedSt}>· {i.creator}</span>}
                <span style={badgeSt(false)}>{ownerLabel(LIFE_KINDS, i.kind)}</span>
                <span style={badgeSt(i.status === 'finished')}>{ownerLabel(LIFE_STATUSES, i.status)}</span>
                <LevelBadge level={i.level} />
                {['needs_ocr', 'partial', 'failed'].includes(i.contentStatus) && <span style={{ ...badgeSt(false), color: '#9A6212' }}>{ownerLabel(CONTENT_STATUSES, i.contentStatus)}</span>}
                {i.excludeFromAI && <span style={badgeSt(false)}>fuera de la IA</span>}
              </div>
              <div style={{ ...mutedSt, marginTop: 4 }}>
                {[...i.topics, ...i.tags].length ? `${[...i.topics, ...i.tags].join(', ')} · ` : ''}
                {i.counts.excerpts} fragmentos · {i.counts.content ? `${i.counts.content} partes de contenido · ` : ''}{i.notesChars ? 'con notas · ' : ''}{i.counts.applications} aplicaciones · actualizado {fmtDate(i.updatedAt || i.createdAt)}
              </div>
            </button>
          ))}
      </div>
    </div>
  );
}
